import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Stack,
  Checkbox,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useNavigate } from "react-router-dom";
import { createPortfolioFromWatchlist1 } from "../../../api/portfolio_api";
import { fmtNumber } from "../Portfolio/portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency";

const MAX_NAME = 50;
const today = new Date().toISOString().split("T")[0];
const market = sessionStorage.getItem("selectedMarket") ?? "US";

// const fmtNumber = (v, d = 2) =>
//   v === null || v === undefined || isNaN(v)
//     ? "—"
//     : Number(v).toLocaleString(undefined, {
//       minimumFractionDigits: d,
//       maximumFractionDigits: d,
//     });

const CreatePortfolioFromWatchlist = ({ open, onClose, watchlist, onCreated }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [holdings, setHoldings] = useState([]);
  const [tab, setTab] = useState(0); // 0 = Equal Weights, 1 = Custom
  const [totalCostBasis, setTotalCostBasis] = useState("");
  const [sharesErrors, setSharesErrors] = useState({});
  const sym = getCurrencySymbol();

  useEffect(() => {
    if (open && watchlist) {
      setName(`${watchlist.name} Portfolio`);
      setError("");
      setTab(0);
      setTotalCostBasis("");
      setSharesErrors({});

      const initialHoldings = (watchlist.stocks || []).map((stock) => ({
        ticker: stock.ticker,
        company_name: stock.company_name,
        current_price: Number(stock.current_price || 0),
        purchase_price: Number(stock.current_price || 0),
        shares: "",
        cost_basis: Number(stock.current_price || 0),
        selected: true,
      }));

      setHoldings(initialHoldings);
    }
  }, [open, watchlist]);

  const isEqualWeight = tab === 0;
  const isCustom = tab === 1;

  const handleToggleAll = () => {
    const allSelected = holdings.every((h) => h.selected);
    setHoldings(holdings.map((h) => ({ ...h, selected: !allSelected })));
  };

  const handleToggleStock = (ticker) => {
    setHoldings(
      holdings.map((h) =>
        h.ticker === ticker ? { ...h, selected: !h.selected } : h
      )
    );
    setSharesErrors((e) => {
      const n = { ...e };
      delete n[ticker];
      return n;
    });
  };

  const handleUpdateShares = (ticker, raw) => {
    if (raw === "" || /^[1-9]\d*$/.test(raw)) {
      setHoldings(
        holdings.map((h) =>
          h.ticker === ticker ? { ...h, shares: raw } : h
        )
      );
      setSharesErrors((e) => ({ ...e, [ticker]: false }));
    }
  };

  const handleUpdateCostBasis = (ticker, costBasis) => {
    const numCost = Math.max(0.01, Number(costBasis) || 0);
    setHoldings(
      holdings.map((h) =>
        h.ticker === ticker ? { ...h, cost_basis: numCost } : h
      )
    );
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) { setError("Portfolio name is required"); return; }
    if (trimmedName.length < 2) { setError("Portfolio name must be at least 2 characters"); return; }

    const selectedHoldings = holdings.filter((h) => h.selected);
    if (selectedHoldings.length === 0) { setError("Please select at least one stock"); return; }
    if (isEqualWeight && (!totalCostBasis || Number(totalCostBasis) <= 0)) {
      setError("Please enter a valid total cost basis"); return;
    }

    if (isCustom) {
      const errors = {};
      selectedHoldings.forEach((h) => {
        if (h.shares === "" || Number(h.shares) < 1) errors[h.ticker] = true;
      });
      if (Object.keys(errors).length > 0) {
        setSharesErrors(errors);
        setError("Please enter a valid quantity for all selected stocks");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const tickers = selectedHoldings.map((h) => h.ticker).join(", ");
      const rebalance = isEqualWeight;
      const portfolioCostBasis = isEqualWeight
        ? Number(totalCostBasis)
        : selectedHoldings.reduce((sum, h) => sum + h.cost_basis, 0);

      const body = {
        name: trimmedName,
        market_code: market,
        holdings: isEqualWeight
          ? []
          : selectedHoldings.map((h) => ({
            ticker: h.ticker,
            shares: Number(h.shares),
            cost_basis: h.cost_basis * Number(h.shares),
            purchase_date: today,
          })),
      };

      const res = await createPortfolioFromWatchlist1({
        rebalance,
        portfolio_costbasis: portfolioCostBasis,
        tickers,
        body,
      });

      if (!res?.success) {
        setError(res?.error?.message || "Failed to create portfolio");
        setLoading(false);
        return;
      }

      onCreated?.(res.data);
      onClose();
    } catch (err) {
      setError("Failed to create portfolio");
    }

    setLoading(false);
  };

  const handleOpenFrontier = () => {
    onClose();
    navigate(`/frontierSimulator?watchlistId=${watchlist?.id}`);
  };

  const selectedCount = holdings.filter((h) => h.selected).length;
  const allSelected = holdings.length > 0 && holdings.every((h) => h.selected);
  const totalValue = isCustom
    ? holdings.filter((h) => h.selected).reduce((sum, h) => sum + (h.cost_basis * h.shares), 0)
    : Number(totalCostBasis) || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)",
          backgroundImage: "none",
          borderRadius: 1.5,
          border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-xl)",
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>

        {/* ── HEADER ── */}
        <Box
          sx={{
            px: 2.5, pt: 1.75, pb: 1.5,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1,
                bgcolor: "var(--color-surface-hover)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <FolderIcon sx={{ fontSize: 16, color: "var(--color-success)" }} />
            </Box>
            <Typography variant="sectionTitle" color="var(--color-text-primary)">
              Create portfolio from watchlist
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              startIcon={<ShowChartIcon sx={{ fontSize: 16 }} />}
              onClick={handleOpenFrontier}
              disabled={!watchlist?.id}
              sx={{
                textTransform: "none", fontWeight: 600,
                color: "var(--color-primary)",
                bgcolor: "var(--color-primary-lighter)",
                border: "1px solid var(--color-primary)",
                borderRadius: 1, px: 1.5, py: 0.5,
                typography: "label",
                "&:hover": { bgcolor: "var(--color-primary)", color: "#fff" },
                "&.Mui-disabled": {
                  color: "var(--color-text-muted)",
                  bgcolor: "var(--color-surface-hover)",
                  borderColor: "var(--color-border-primary)",
                },
              }}
            >
              Check Efficient Frontier
            </Button>

            <IconButton
              onClick={onClose} size="small"
              sx={{
                color: "var(--color-text-muted)", p: 0.5,
                "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>
          <Typography variant="paragraph" color="var(--color-text-secondary)" sx={{ mb: 2.5, display: "block" }}>
            Create a portfolio from your watchlist. Select the stocks you want to
            include and choose how to allocate weights.
          </Typography>

          {/* ── Portfolio Name ── */}
          <Box mb={2.5}>
            <Typography variant="tableHeader" color="var(--color-text-primary)" sx={{ display: "block", mb: 0.75 }}>
              Portfolio name
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder="e.g., Tech Stocks, Retirement Fund"
              value={name}
              inputProps={{ maxLength: MAX_NAME }}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              helperText={
                <Box component="span" sx={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)" }}></span>
                  <span style={{ color: "var(--color-text-muted)" }}>{name.length}/{MAX_NAME}</span>
                </Box>
              }
              InputProps={{
                sx: {
                  borderRadius: 1,
                  bgcolor: "var(--color-bg-secondary)",
                  color: "var(--color-text-primary)",
                  "& input::placeholder": { color: "var(--color-text-muted)", opacity: 1 },
                  "& fieldset": { borderColor: "var(--color-border-primary)" },
                  "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
                  "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
                },
              }}
              FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
              autoFocus
            />
          </Box>
          {error && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px solid var(--color-error)",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  mt: "5px",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "var(--color-error)",
                  flexShrink: 0,
                }}
              />
              <Typography variant="tableHeader" color="var(--color-error)" sx={{ lineHeight: 1.6 }}>
                {error}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2, borderColor: "var(--color-border-primary)" }} />

          {/* ── Tabs ── */}
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(""); setSharesErrors({}); }}
            sx={{
              minHeight: 36, mb: 2,
              "& .MuiTabs-indicator": { bgcolor: "var(--color-primary)", height: 2 },
            }}
          >
            {["Equal Weights", "Custom"].map((label) => (
              <Tab
                key={label} label={label}
                sx={{
                  textTransform: "none", fontWeight: 600,
                  minHeight: 36, px: 2,
                  color: "var(--color-text-secondary)",
                  "&.Mui-selected": { color: "var(--color-primary)" },
                  typography: "tableHeader",
                }}
              />
            ))}
          </Tabs>

          {/* ── Equal Weight: Total Cost Basis ── */}
          {isEqualWeight && (
            <Box mb={2}>
              <Typography variant="tableHeader" color="var(--color-text-primary)" sx={{ display: "block", mb: 0.75 }}>
                Total Cost Basis
              </Typography>
              <TextField
                fullWidth size="small" type="number"
                placeholder="e.g., 10000"
                value={totalCostBasis}
                onChange={(e) => { setTotalCostBasis(e.target.value); setError(""); }}
                inputProps={{ min: 1, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    // <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mr: 0.5 }}>$</Typography>
                    <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mr: 0.5 }}>{sym}</Typography>
                  ),
                  sx: {
                    borderRadius: 1,
                    bgcolor: "var(--color-bg-secondary)",
                    color: "var(--color-text-primary)",
                    "& fieldset": { borderColor: "var(--color-border-primary)" },
                    "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
                    "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
                  },
                }}
                helperText={
                  <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                    This amount will be equally distributed across the selected stocks
                  </span>
                }
                FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
              />
            </Box>
          )}

          {/* ── Stock Selection Header ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography
              variant="tableHeader"
              color="var(--color-text-primary)"
              sx={{ textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}
            >
              Select Stocks ({selectedCount}/{holdings.length})
            </Typography>
            <Button
              size="small" onClick={handleToggleAll}
              sx={{
                fontWeight: 600, color: "var(--color-primary)",
                textTransform: "none", minWidth: "auto", p: 0.5,
                typography: "tableHeader",
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              {allSelected ? "Unselect All" : "Select All"}
            </Button>
          </Box>

          {/* ── Stock List ── */}
          <Box
            sx={{
              maxHeight: 320, overflowY: "auto",
              bgcolor: "var(--color-bg-secondary)",
              borderRadius: 1,
              border: "1px solid var(--color-border-primary)",
              mb: 2,
            }}
          >
            {holdings.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography variant="tableHeader" color="var(--color-text-muted)">
                  No stocks in this watchlist
                </Typography>
              </Box>
            ) : (
              holdings.map((holding, idx) => (
                <Box
                  key={holding.ticker}
                  sx={{
                    p: 1.5,
                    borderBottom: idx < holdings.length - 1 ? "1px solid var(--color-surface)" : "none",
                    display: "flex", alignItems: "center", gap: 1.5,
                    transition: "all 0.15s ease",
                    "&:hover": { bgcolor: "var(--color-surface)" },
                  }}
                >
                  <Checkbox
                    checked={holding.selected}
                    onChange={() => handleToggleStock(holding.ticker)}
                    icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                    checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                    sx={{ color: "var(--color-text-muted)", p: 0, "&.Mui-checked": { color: "var(--color-primary)" } }}
                  />

                  <Box flex={1}>
                    <Typography variant="subtitle" color="var(--color-text-primary)" sx={{ display: "block" }}>
                      {holding.ticker}
                    </Typography>
                    <Typography variant="tableHeader" color="var(--color-text-muted)" noWrap>
                      {holding.company_name}
                    </Typography>
                  </Box>

                  {/* Current Price */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 85 }}>
                    <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mb: 0.25 }}>
                      Current Price
                    </Typography>
                    <Typography variant="tableHeader" color="var(--color-text-primary)" sx={{ fontWeight: 600 }}>
                      {/* ${fmtNumber(holding.current_price, 2)} */}
                      {sym}{fmtNumber(holding.current_price, 2)}
                    </Typography>
                  </Box>

                  {/* ── Custom: Shares + Cost Basis ── */}
                  {isCustom && (
                    <>
                      {/* Quantity */}
                      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 80 }}>
                        <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mb: 0.25 }}>
                          Quantity
                        </Typography>
                        <TextField
                          type="text"
                          inputMode="numeric"
                          size="small"
                          placeholder="e.g. 10"
                          value={holding.shares}
                          disabled={!holding.selected}
                          error={!!sharesErrors[holding.ticker]}
                          onChange={(e) => handleUpdateShares(holding.ticker, e.target.value)}
                          InputProps={{
                            sx: {
                              borderRadius: 1,
                              bgcolor: holding.selected ? "var(--color-surface)" : "var(--color-bg-secondary)",
                              color: "var(--color-text-primary)",
                              width: 90,
                              "& input::placeholder": { color: "var(--color-text-muted)", opacity: 0.3 },
                              "& fieldset": {
                                borderColor: sharesErrors[holding.ticker]
                                  ? "var(--color-error)"
                                  : "var(--color-border-primary)",
                              },
                              "&:hover fieldset": {
                                borderColor: sharesErrors[holding.ticker]
                                  ? "var(--color-error)"
                                  : holding.selected ? "var(--color-border-secondary)" : "var(--color-border-primary)",
                              },
                              "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
                              "&.Mui-disabled": {
                                bgcolor: "var(--color-bg-secondary)",
                                "& fieldset": { borderColor: "var(--color-border-primary)" },
                              },
                            },
                          }}
                        />
                        {sharesErrors[holding.ticker] && (
                          <Typography variant="caption" color="var(--color-error)" sx={{ mt: 0.4, lineHeight: 1.3 }}>
                            Required
                          </Typography>
                        )}
                      </Box>

                      {/* Cost Basis */}
                      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 100 }}>
                        <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mb: 0.25 }}>
                          Cost Basis
                        </Typography>
                        <TextField
                          type="number" size="small"
                          value={holding.cost_basis}
                          onChange={(e) => handleUpdateCostBasis(holding.ticker, e.target.value)}
                          disabled={!holding.selected}
                          inputProps={{ min: 0.01, step: "0.01", style: { textAlign: "right" } }}
                          InputProps={{
                            startAdornment: (
                              // <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mr: 0.25 }}>$</Typography>
                              <Typography variant="tableHeader" color="var(--color-text-muted)" sx={{ mr: 0.25 }}>{sym}</Typography>
                            ),
                            sx: {
                              borderRadius: 1,
                              bgcolor: holding.selected ? "var(--color-surface)" : "var(--color-bg-secondary)",
                              color: "var(--color-text-primary)",
                              width: 110,
                              "& fieldset": { borderColor: "var(--color-border-primary)" },
                              "&:hover fieldset": { borderColor: holding.selected ? "var(--color-border-secondary)" : "var(--color-border-primary)" },
                              "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
                              "&.Mui-disabled": {
                                bgcolor: "var(--color-bg-secondary)",
                                "& fieldset": { borderColor: "var(--color-border-primary)" },
                              },
                            },
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              ))
            )}
          </Box>

          {/* ── Summary ── */}
          {selectedCount > 0 && (
            <Box
              sx={{
                bgcolor: "var(--color-surface-hover)", borderRadius: 1, p: 1.5, mb: 2,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="tableHeader" color="var(--color-text-secondary)" sx={{ display: "block", mb: 0.25 }}>
                  Selected Holdings
                </Typography>
                <Typography variant="tableHeader" color="var(--color-text-primary)" sx={{ fontWeight: 600 }}>
                  {selectedCount} stock{selectedCount !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="tableHeader" color="var(--color-text-secondary)" sx={{ display: "block", mb: 0.25 }}>
                  Total Cost Basis
                </Typography>
                <Typography variant="tableHeader" color="var(--color-success)" sx={{ fontWeight: 700 }}>
                  {/* ${fmtNumber(totalValue, 2)} */}
                  {sym}{fmtNumber(totalValue, 2)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── Action Buttons ── */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" onClick={onClose} disabled={loading} fullWidth
              sx={{
                textTransform: "none", fontSize: 15, fontWeight: 500,
                color: "var(--color-text-secondary)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth variant="contained"
              disabled={
                loading ||
                name.trim().length < 2 ||
                selectedCount === 0 ||
                (isEqualWeight && (!totalCostBasis || Number(totalCostBasis) <= 0))
              }
              onClick={handleCreate}
              sx={{
                textTransform: "none", fontSize: 15, fontWeight: 500,
                bgcolor: "var(--color-primary)", boxShadow: "none",
                "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
              }}
            >
              {loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                `Create portfolio with ${selectedCount} stock${selectedCount !== 1 ? "s" : ""}`
              )}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePortfolioFromWatchlist;